/**
 * Глобальный фильтр периода (date_from / date_to) + регион — общий для
 * всех страниц. Хранится в URL search params, переживает reload.
 *
 * Использование:
 *   const { from, to, region } = usePeriod();
 *   useQuery({ queryKey: ["overview", from, to, region], queryFn: () => fetchOverview({date_from: from, date_to: to, region}) });
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type PeriodPreset = "today" | "week" | "prev_week" | "month" | "this_month" | "prev_month" | "quarter" | "year" | "ytd" | "custom";

export type Region = "all" | "Москва" | "Санкт-Петербург";

interface PeriodCtx {
  from: string;       // YYYY-MM-DD
  to: string;         // YYYY-MM-DD
  preset: PeriodPreset;
  setPreset: (p: PeriodPreset) => void;
  setRange: (from: string, to: string) => void;
  label: string;
  region: Region;
  setRegion: (r: Region) => void;
}

const PeriodContext = createContext<PeriodCtx | null>(null);

function todayMSK(): Date {
  // МСК = UTC+3
  const now = new Date();
  return new Date(now.getTime() + 3 * 60 * 60 * 1000);
}

function fmtIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function presetRange(p: PeriodPreset): [string, string] {
  const today = todayMSK();
  const todayStr = fmtIso(today);
  if (p === "today") return [todayStr, todayStr];
  if (p === "week") {
    const weekAgo = new Date(today.getTime() - 6 * 86400 * 1000);
    return [fmtIso(weekAgo), todayStr];
  }
  if (p === "month") {
    const monthAgo = new Date(today.getTime() - 29 * 86400 * 1000);
    return [fmtIso(monthAgo), todayStr];
  }
  if (p === "prev_week") {
    // Календарная прошлая неделя: понедельник — воскресенье.
    const dow = (today.getUTCDay() + 6) % 7;           // 0 = понедельник
    const thisMon = new Date(today.getTime() - dow * 86400 * 1000);
    const prevMon = new Date(thisMon.getTime() - 7 * 86400 * 1000);
    const prevSun = new Date(thisMon.getTime() - 1 * 86400 * 1000);
    return [fmtIso(prevMon), fmtIso(prevSun)];
  }
  if (p === "this_month") {
    // Календарный текущий месяц: 1-е число — сегодня.
    const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    return [fmtIso(first), todayStr];
  }
  if (p === "prev_month") {
    // Календарный прошлый месяц: 1-е — последнее число.
    const y = today.getUTCFullYear();
    const m = today.getUTCMonth();
    const first = new Date(Date.UTC(y, m - 1, 1));
    const last = new Date(Date.UTC(y, m, 0));
    return [fmtIso(first), fmtIso(last)];
  }
  if (p === "quarter") {
    const qAgo = new Date(today.getTime() - 89 * 86400 * 1000);
    return [fmtIso(qAgo), todayStr];
  }
  if (p === "year") {
    const yAgo = new Date(today.getTime() - 364 * 86400 * 1000);
    return [fmtIso(yAgo), todayStr];
  }
  if (p === "ytd") {
    const yStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
    return [fmtIso(yStart), todayStr];
  }
  return [todayStr, todayStr];
}

function readUrl(): { from: string; to: string; preset: PeriodPreset; region: Region } {
  const params = new URLSearchParams(window.location.search);
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const preset = (params.get("preset") as PeriodPreset) || "week";
  const regionParam = params.get("region");
  const region: Region = (regionParam === "Москва" || regionParam === "Санкт-Петербург") ? regionParam : "all";
  if (preset === "custom" && from && to) {
    return { from, to, preset, region };
  }
  const [pf, pt] = presetRange(preset === "custom" ? "week" : preset);
  return { from: pf, to: pt, preset: preset === "custom" ? "week" : preset, region };
}

function writeUrl(from: string, to: string, preset: PeriodPreset, region: Region) {
  // BrixChat (brixchat.ru) — только мессенджер: период дашборда в адрес не пишем, адрес остаётся «/».
  if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(window.location.hostname)) return;
  const params = new URLSearchParams(window.location.search);
  params.set("preset", preset);
  if (preset === "custom") {
    params.set("from", from);
    params.set("to", to);
  } else {
    params.delete("from");
    params.delete("to");
  }
  if (region !== "all") params.set("region", region);
  else params.delete("region");
  const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
  window.history.replaceState({}, "", newUrl);
}

export function PeriodProvider({ children }: { children: ReactNode }) {
  const initial = readUrl();
  const [preset, setPresetState] = useState<PeriodPreset>(initial.preset);
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [region, setRegionState] = useState<Region>(initial.region);

  const setPreset = useCallback((p: PeriodPreset) => {
    setPresetState(p);
    if (p !== "custom") {
      const [f, t] = presetRange(p);
      setFrom(f);
      setTo(t);
      writeUrl(f, t, p, region);
    }
  }, [region]);

  const setRange = useCallback((f: string, t: string) => {
    setPresetState("custom");
    setFrom(f);
    setTo(t);
    writeUrl(f, t, "custom", region);
  }, [region]);

  const setRegion = useCallback((r: Region) => {
    setRegionState(r);
    writeUrl(from, to, preset, r);
  }, [from, to, preset]);

  // Обновляем URL при первом монтировании если пустой
  useEffect(() => { writeUrl(from, to, preset, region); /* eslint-disable-next-line */ }, []);

  const label = useMemo(() => {
    const map: Record<PeriodPreset, string> = {
      today: "Сегодня",
      week: "7 дней",
      prev_week: "Прошлая неделя",
      month: "30 дней",
      this_month: "Этот месяц",
      prev_month: "Прошлый месяц",
      quarter: "Квартал",
      year: "Год",
      ytd: "С начала года",
      custom: "—",
    };
    if (preset !== "custom") return map[preset];
    const f = from.slice(8, 10) + "." + from.slice(5, 7) + "." + from.slice(2, 4);
    const t = to.slice(8, 10) + "." + to.slice(5, 7) + "." + to.slice(2, 4);
    return `${f} — ${t}`;
  }, [preset, from, to]);

  return (
    <PeriodContext.Provider value={{ from, to, preset, setPreset, setRange, label, region, setRegion }}>
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriod(): PeriodCtx {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriod must be used within PeriodProvider");
  return ctx;
}
